import { ArrowRight, Lock, Search } from "lucide-react";
import { useCallback, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { incompleteNotice, probe, probeOr, unreachableNames } from "@/lib/probe";
import type { AuthDecisionEntry, ToolTraceEntry, PermissionDecisionEntry, PromptContextEntry } from "@/types/audit";

const PIPELINE_STAGES = [
  { id: "gateway", label: "Gateway", desc: "WS frame parse, session resolve" },
  { id: "auth", label: "Auth", desc: "JWT validation", hook: "inbound_claim" },
  { id: "dispatch", label: "Dispatch", desc: "Route, skill detect" },
  { id: "orchestration", label: "Orchestration", desc: "Retry loop, compaction" },
  { id: "prompt", label: "Prompt", desc: "System prompt + tools", hook: "before_prompt_build" },
  { id: "llm", label: "LLM", desc: "API call, streaming" },
  { id: "tools", label: "Tools", desc: "Execution + permissions", hook: "before_tool_call" },
  { id: "delivery", label: "Delivery", desc: "Response + persist" },
];

const PERMISSION_MATRIX = [
  { role: "OWNER", read: true, write: true, draft: true, system: true, unknownLx: true },
  { role: "HANDLER", read: true, write: true, draft: true, system: true, unknownLx: false },
  { role: "CONTRIBUTOR", read: true, write: "own", draft: false, system: true, unknownLx: false },
  { role: "OBSERVER", read: true, write: false, draft: false, system: true, unknownLx: false },
] as const;

type SessionAuditTrail = {
  auth: AuthDecisionEntry[];
  tools: ToolTraceEntry[];
  permissions: PermissionDecisionEntry[];
  prompts: PromptContextEntry[];
};

export function PipelineInspector() {
  const { rpc, state } = useGatewayRpc();
  const [sessionKey, setSessionKey] = useState("");
  const [trail, setTrail] = useState<SessionAuditTrail | null>(null);
  /** Probe labels that failed on the last lookup — an empty trail must be
   *  distinguishable from a trail we could not read. */
  const [unreachable, setUnreachable] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const lookupSession = useCallback(async () => {
    if (!sessionKey.trim() || state !== "connected") { return; }
    setLoading(true);
    try {
      // Settled probes (lib/probe.ts) — an empty trail must not be able to
      // mean "the rpc failed"; an operator inspecting a session would read
      // it as "this session did nothing".
      const [auth, tools, permissions, prompts] = await Promise.all([
        probe(rpc<AuthDecisionEntry[]>("audit.auth", { sessionKey })),
        probe(rpc<ToolTraceEntry[]>("audit.tools", { sessionKey })),
        probe(rpc<PermissionDecisionEntry[]>("audit.permissions", { sessionKey })),
        probe(rpc<PromptContextEntry[]>("audit.prompts", { sessionKey })),
      ]);
      setUnreachable(unreachableNames({ auth, tools, permissions, prompts }));
      setTrail({
        auth: probeOr(auth, [] as AuthDecisionEntry[]),
        tools: probeOr(tools, [] as ToolTraceEntry[]),
        permissions: probeOr(permissions, [] as PermissionDecisionEntry[]),
        prompts: probeOr(prompts, [] as PromptContextEntry[]),
      });
    } catch {
      setUnreachable(["auth", "tools", "permissions", "prompts"]);
      setTrail({ auth: [], tools: [], permissions: [], prompts: [] });
    } finally {
      setLoading(false);
    }
  }, [rpc, state, sessionKey]);

  if (state !== "connected") {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold">Pipeline Inspector</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {state === "connecting" ? "Connecting to gateway..." : "Gateway disconnected"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Pipeline Inspector</h1>

      {/* Pipeline Stages */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">8-Stage Message Pipeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-1 overflow-x-auto pb-2">
            {PIPELINE_STAGES.map((stage, i) => (
              <div key={stage.id} className="flex items-center gap-1">
                <div className="relative flex flex-col items-center rounded-lg border border-slate-200 px-3 py-2 text-center dark:border-slate-700" style={{ minWidth: 90 }}>
                  <span className="text-xs font-semibold">{stage.label}</span>
                  <span className="text-[9px] text-muted-foreground">{stage.desc}</span>
                  {stage.hook && (
                    <div className="absolute -top-1.5 right-1 h-3 w-3 rounded-full bg-indigo-500" title={stage.hook} />
                  )}
                </div>
                {i < PIPELINE_STAGES.length - 1 && <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-indigo-500" /> Lexiun hook</span>
            <span>3 active hooks: inbound_claim (Auth), before_prompt_build (Prompt), before_tool_call (Tools)</span>
          </div>
        </CardContent>
      </Card>

      {/* Session Lookup */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Session Audit Trail</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <input value={sessionKey} onChange={(e) => setSessionKey(e.target.value)}
              placeholder="Enter session key (e.g. agent:main:lx:case:...)"
              onKeyDown={(e) => { if (e.key === "Enter") { lookupSession(); } }}
              className="flex-1 rounded-md border border-slate-200 bg-transparent px-3 py-1.5 font-mono text-xs outline-none focus:border-indigo-400 dark:border-slate-700" />
            <button type="button" onClick={lookupSession} disabled={loading}
              className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              <Search className="h-3.5 w-3.5" /> Lookup
            </button>
          </div>

          {trail && (
            <div className="mt-4 space-y-3">
              {/* Must sit ABOVE the empty state: "No audit data for this
                  session key" is a claim about the SESSION, and we may only
                  make it when we actually managed to read every source. */}
              {incompleteNotice(unreachable, 4) && (
                <div
                  className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
                  data-testid="pipeline-inspector-incomplete"
                  role="status"
                >
                  {incompleteNotice(unreachable, 4)}
                </div>
              )}
              {trail.auth.length === 0 && trail.tools.length === 0 && trail.prompts.length === 0 ? (
                <p className="py-4 text-center text-sm text-slate-400">
                  {unreachable.length > 0
                    ? "Could not read this session's audit trail — see above."
                    : "No audit data for this session key"}
                </p>
              ) : (
                <>
                  {/* Auth decisions */}
                  {trail.auth.length > 0 && (
                    <div>
                      <p className="mb-1 text-xs font-medium text-muted-foreground">Auth ({trail.auth.length})</p>
                      {trail.auth.map((a, i) => (
                        <div key={i} className="flex items-center gap-2 rounded border p-1.5 text-xs dark:border-slate-800">
                          <Badge variant={a.decision === "allow" || a.decision === "dev_mode" ? "default" : "destructive"} className="text-[9px]">{a.decision}</Badge>
                          <span>{a.role ?? "—"}</span>
                          <span className="text-muted-foreground">{new Date(a.ts).toLocaleTimeString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Prompt contexts */}
                  {trail.prompts.length > 0 && (
                    <div>
                      <p className="mb-1 text-xs font-medium text-muted-foreground">Prompt Context ({trail.prompts.length})</p>
                      {trail.prompts.map((p, i) => (
                        <div key={i} className="rounded border p-2 text-xs dark:border-slate-800">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[9px]">{p.contextType}</Badge>
                            <span>{p.role}</span>
                            <span className="text-muted-foreground">{p.prependContextLength} chars</span>
                          </div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {p.injectedBlocks.map((b) => <Badge key={b} variant="secondary" className="text-[9px]">{b}</Badge>)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Tool traces */}
                  {trail.tools.length > 0 && (
                    <div>
                      <p className="mb-1 text-xs font-medium text-muted-foreground">Tool Calls ({trail.tools.length})</p>
                      {trail.tools.map((t, i) => (
                        <div key={i} className="flex items-center gap-2 rounded border p-1.5 text-xs dark:border-slate-800">
                          <Badge variant={t.decision === "allowed" ? "default" : "destructive"} className="text-[9px]">{t.decision}</Badge>
                          <span className="font-medium">{t.toolName}</span>
                          {t.durationMs != null && <span className="text-muted-foreground">{t.durationMs}ms</span>}
                          {t.error && <span className="text-red-400">{t.error}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permission Matrix */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Lock className="h-4 w-4" /> Permission Matrix
          </CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 dark:border-slate-700">
                <th className="pb-2 pr-4">Role</th>
                <th className="pb-2 pr-4 text-center">Read</th>
                <th className="pb-2 pr-4 text-center">Write</th>
                <th className="pb-2 pr-4 text-center">Draft</th>
                <th className="pb-2 pr-4 text-center">System</th>
                <th className="pb-2 text-center">Unknown lx_*</th>
              </tr>
            </thead>
            <tbody>
              {PERMISSION_MATRIX.map((row) => (
                <tr key={row.role} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="py-2 pr-4 font-medium">{row.role}</td>
                  {[row.read, row.write, row.draft, row.system, row.unknownLx].map((val, i) => (
                    <td key={i} className="py-2 pr-4 text-center">
                      {val === true ? <span className="text-green-500">✓</span>
                        : val === false ? <span className="text-red-400">✗</span>
                        : <span className="text-amber-500">own</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
